import { useState, useEffect, useCallback } from "react";
import { lumi } from "@/lib/lumi";
import { jobs as fallbackJobs } from "@/data/jobs";
import type { Job } from "@/data/jobs";
import { notifyJobAlertSubscribers } from "@/lib/jobAlertEmail";

// DB record extends the static Job shape with Mongo _id + audit fields.
export interface JobRecord extends Omit<Job, "id"> {
  _id: string;
  creator?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface JobInput {
  title: string;
  company: string;
  location: string;
  state: string;
  type: Job["type"];
  mineral: Job["mineral"];
  salary: string;
  salaryNum: number;
  experience: Job["experience"];
  posted?: string;
  tags?: string[];
  featured?: boolean;
  logo?: string;
  description?: string;
}

interface ListOptions {
  filter?: Record<string, unknown>;
  sort?: Record<string, 1 | -1>;
  limit?: number;
  skip?: number;
}

// Map static fallback data into the JobRecord shape so the UI renders
// even if the DB is unreachable on first load.
const fallbackRecords: JobRecord[] = fallbackJobs.map((j) => ({
  ...j,
  _id: j.id,
}));

export function useJobs(initialOptions?: ListOptions) {
  const [jobs, setJobs] = useState<JobRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);

  const fetchJobs = useCallback(async (options?: ListOptions) => {
    setLoading(true);
    setError(null);
    try {
      const { list, total } = await lumi.entities.jobs.list({
        sort: { createdAt: -1 },
        ...options,
      });
      if (Array.isArray(list) && list.length > 0) {
        setJobs(list as JobRecord[]);
        setTotal(typeof total === "number" ? total : list.length);
        setUsingFallback(false);
      } else {
        // Empty DB — show static fallback so the board is never blank.
        setJobs(fallbackRecords);
        setTotal(fallbackRecords.length);
        setUsingFallback(true);
      }
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
      setError("Could not load jobs from the database.");
      setJobs(fallbackRecords);
      setTotal(fallbackRecords.length);
      setUsingFallback(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs(initialOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createJob = useCallback(
    async (input: JobInput) => {
      const now = new Date().toISOString();
      const user = await lumi.auth.refreshUser().catch(() => null);
      const payload = {
        ...input,
        posted: input.posted || "Just now",
        tags: input.tags || [],
        featured: input.featured ?? false,
        logo: input.logo || input.company.slice(0, 2).toUpperCase(),
        description: input.description || "",
        creator: user?.userId || "admin",
        createdAt: now,
        updatedAt: now,
      };
      const created = await lumi.entities.jobs.create(payload);
      try {
        await notifyJobAlertSubscribers(input);
      } catch (err) {
        console.error("Job alert notification failed:", err);
      }
      await fetchJobs();
      return created;
    },
    [fetchJobs]
  );

  const updateJob = useCallback(
    async (id: string, updates: Partial<JobInput>) => {
      if (typeof id !== "string") throw new Error("Job ID must be a string");
      const updated = await lumi.entities.jobs.update(id, {
        ...updates,
        updatedAt: new Date().toISOString(),
      });
      await fetchJobs();
      return updated;
    },
    [fetchJobs]
  );

  const deleteJob = useCallback(
    async (id: string) => {
      if (typeof id !== "string" || id === "[object Object]") {
        throw new Error("Invalid job ID: must be a string");
      }
      await lumi.entities.jobs.delete(id);
      await fetchJobs();
    },
    [fetchJobs]
  );

  return {
    jobs,
    total,
    loading,
    error,
    usingFallback,
    fetchJobs,
    createJob,
    updateJob,
    deleteJob,
  };
}
